import { useContext, useState } from 'react'
import { useFormikContext } from 'formik'
import { FormContent, ButtonDrawer, FormError } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { addEmail, completeActivation, isBadState } from '~/lib/api'
import { emitCustomEvent } from 'react-custom-events'
import { useTranslation } from 'react-i18next'

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

export default function SocialSelectEmail() {
  const { t } = useTranslation()
  const { next } = useContext(StepperContext)

  const { loginAndFetchBookings } = useContext(StoreContext)
  const { values, setFieldValue, setFieldError } = useFormikContext()
  const [isLoading, setIsLoading] = useState(false)
  const [activationBadState, setActivationBadState] = useState(false)
  const [localError, setLocalError] = useState(null)

  const completeLogin = () => {
    completeActivation({ termsAndConditions: true })
      .then(a => {
        if (a.userId) {
          loginAndFetchBookings(a.userId)
          emitCustomEvent('SHOW_LOGIN', false)
        }
      })
      .catch(err => setLocalError(err?.response?.message))
  }

  const handleResponse = (response) => {
    if (response?.deviceActivationState === 'EMAIL_OTP_INITIATED') {
      setFieldValue('maskedEmail', response.maskedEmail || values.maskedEmail, false)
      next('validateEmail')
    } else {
      completeLogin()
    }
  }

  const handleUseExisting = () => {
    setIsLoading(true)
    addEmail(values.maskedEmail)
      .then(handleResponse)
      .catch(error => {
        setFieldError('email', error?.response?.message)
        if (isBadState(error)) setActivationBadState(true)
      })
      .finally(() => setIsLoading(false))
  }

  const handleNotMe = () => {
    // Same reason as in ConfirmExistingProfile: firstName/lastName may hold the matched
    // profile's name — ValidatePhone copies firstName out of the response's userInfo on
    // the way to this screen — and InputName renders from these fields, so without this
    // the other person's name is pre-filled and saved onto the new profile.
    setFieldValue('firstName', '', false)
    setFieldValue('lastName', '', false)

    const googleEmail = values.externalEmail
    if (!googleEmail) {
      setLocalError('No external email available')
      return
    }
    setIsLoading(true)
    setLocalError(null)
    addEmail(googleEmail)
      .then(response => {
        console.log('[SocialSelectEmail] handleNotMe response:', response)
        const actions = response?.expectedActions || []
        if (response?.userId) {
          completeLogin()
        } else if (response?.deviceActivationState === 'EMAIL_OTP_INITIATED') {
          setFieldValue('maskedEmail', response.maskedEmail || '', false)
          next('validateEmail')
        } else if (response?.deviceActivationState === 'EMAIL_ADDED') {
          if (actions.includes('ADD_NAME')) {
            next('inputName')
          } else {
            completeActivation({ termsAndConditions: true })
              .then(a => {
                if (a.userId) {
                  loginAndFetchBookings(a.userId)
                  emitCustomEvent('SHOW_LOGIN', false)
                }
              })
              .catch(err => setLocalError(err?.response?.message))
          }
        } else {
          console.warn('[SocialSelectEmail] Unhandled state:', response?.deviceActivationState, actions)
          setLocalError(`Unhandled state: ${response?.deviceActivationState}`)
        }
      })
      .catch(error => {
        console.error('[SocialSelectEmail] handleNotMe error:', error)
        setLocalError(error?.response?.message || error?.message || 'Unknown error')
        if (isBadState(error)) setActivationBadState(true)
      })
      .finally(() => setIsLoading(false))
  }

  const displayName = values.firstName || values.maskedEmail

  const buttons = [
    { func: handleNotMe, title: t('button-create-new-account'), disabled: isLoading || activationBadState },
    { func: handleUseExisting, title: t('button-login'), disabled: isLoading || activationBadState },
  ]

  return (
    <>
      <FormContent isLoading={isLoading}>
        <div className="pt-6 pb-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-[18px] font-bold tracking-[0.54px] m-0">
              {displayName ? t('header-login-as', { name: displayName }) : t('header-is-this-you')}
            </h2>
            <p className="text-body-lg text-on-surface m-0">
              {t('info-existing-account-found')}
            </p>
          </div>

          {(values.firstName || values.maskedEmail) && (
            <div className="flex items-center gap-3 py-1">
              <div className="bg-surface-container p-1 rounded-[24px] shrink-0">
                <div className="flex items-center justify-center w-6 h-6 text-on-surface-variant">
                  <UserIcon />
                </div>
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                {values.firstName && (
                  <p className="text-body-lg font-medium text-on-surface tracking-[-0.34px] m-0 truncate">
                    {values.firstName}
                  </p>
                )}
                {values.maskedEmail && (
                  <p className="text-body-lg text-on-surface-variant m-0 truncate">
                    {values.maskedEmail}
                  </p>
                )}
              </div>
            </div>
          )}

          {localError && <FormError>{localError}</FormError>}
        </div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading} />
    </>
  )
}
